// Frontend technical telemetry: uncaught errors, failed API requests and Core Web Vitals.
// Privacy: only a normalized route, status, duration and a sanitized message are sent — never
// form values, tokens, phone, email or request bodies. Failures here never break the app.
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";
import { analyticsSessionId } from "./analytics";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const MAX_BATCH = 10;
const FLUSH_MS = 4000;
const SENSITIVE = /(\+?\d[\d\s\-/().]{6,})|([\w.+-]+@[\w-]+\.[a-z]{2,})|token|password|otp|card/i;

let queue = [];
let timer = null;
let started = false;
const seen = new Set();

export function normalizeRoute(path) {
  return String(path || window.location.pathname)
    .split("?")[0]
    .split("/")
    .map((s) => (/^([0-9a-fA-F-]{8,}|\d+)$/.test(s) ? ":id" : s))
    .join("/")
    .slice(0, 120) || "/";
}

function flowOf(route) {
  const r = route || "";
  if (r === "/" || r.startsWith("/gradovi")) return "HOMEPAGE";
  if (r.startsWith("/restoran")) return "RESTAURANT_MENU";
  if (r.startsWith("/korpa")) return "CART";
  if (r.startsWith("/checkout") || r.startsWith("/placanje")) return "CHECKOUT";
  if (r.startsWith("/porudzbina")) return "ORDER_TRACKING";
  if (r.startsWith("/restaurant/")) return "RESTAURANT_PANEL";
  if (r.startsWith("/vista")) return "VISTA";
  if (r.startsWith("/b2b")) return "B2B";
  if (r.startsWith("/prijava") || r.startsWith("/login")) return "AUTH";
  return "OTHER";
}

function deviceType() {
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches;
  const w = window.innerWidth;
  return standalone ? "pwa" : w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
}

function browserFamily() {
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  return "Other";
}

function clean(msg) {
  const s = String(msg || "").replace(/["'<>]/g, "").slice(0, 180);
  return SENSITIVE.test(s) ? "REDACTED_MESSAGE" : s;
}

function flush(beacon = false) {
  if (!queue.length) return;
  const body = JSON.stringify({ session_id: analyticsSessionId(), route: normalizeRoute(), events: queue });
  queue = [];
  clearTimeout(timer);
  timer = null;
  try {
    const url = `${API}/tech/events`;
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    }
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true })
      .catch(() => {});
  } catch { /* telemetry must never throw */ }
}

export function reportTech(event) {
  try {
    const route = normalizeRoute(event.route);
    const key = `${event.kind}|${route}|${event.metric || ""}|${event.error_code || ""}|${clean(event.message)}`;
    if (event.kind === "WEB_VITAL" && seen.has(key)) return;
    if (event.kind === "WEB_VITAL") seen.add(key);
    queue.push({
      ...event, route, flow: event.flow || flowOf(normalizeRoute()),
      message: event.message ? clean(event.message) : undefined,
      device_type: deviceType(), browser: browserFamily(),
      display_mode: window.matchMedia?.("(display-mode: standalone)")?.matches ? "standalone" : "browser",
    });
    if (queue.length >= MAX_BATCH) flush();
    else if (!timer) timer = setTimeout(flush, FLUSH_MS);
  } catch { /* no-op */ }
}

/** Failed API request reported from the axios interceptor. */
export function reportApiFailure({ url, method, status, durationMs, code, message }) {
  reportTech({
    kind: "API_FAIL", route: normalizeRoute(String(url || "").replace(/^https?:\/\/[^/]+/, "")),
    method, status: status || undefined, duration_ms: durationMs,
    category: !status ? "NETWORK_ERROR" : status >= 500 ? "SERVER_5XX" :
      status === 401 || status === 403 ? "AUTH_ERROR" : "CLIENT_4XX",
    error_code: code, message,
  });
}

export function initTechTelemetry() {
  if (started) return;
  started = true;
  window.addEventListener("error", (e) => {
    reportTech({ kind: "CLIENT_ERROR", category: "UNKNOWN",
                 error_code: e.error?.name || "Error", message: e.message,
                 extra: { source: `${e.filename || ""}:${e.lineno || 0}`.slice(0, 120) } });
  });
  window.addEventListener("unhandledrejection", (e) => {
    reportTech({ kind: "CLIENT_ERROR", category: "UNKNOWN", error_code: "UnhandledRejection",
                 message: e.reason?.message || String(e.reason || "") });
  });
  window.addEventListener("pagehide", () => flush(true));

  // Core Web Vitals via the official library; route captured at measurement time.
  const vital = (m) => reportTech({ kind: "WEB_VITAL", metric: m.name, value: Math.round(m.value * 1000) / 1000,
                                    rating: m.rating, route: normalizeRoute(), metric_id: m.id });
  try {
    onLCP(vital); onINP(vital); onCLS(vital); onFCP(vital); onTTFB(vital);
  } catch { /* unsupported browser */ }
}
